import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ClientSearch } from "@/components/common/ClientSearch";
import { toast } from "sonner";
import { Gift, Copy, Share2, Users, CheckCircle2, Clock, ChevronDown, RefreshCw, UserCheck, Receipt } from "lucide-react";

// Formatta una data ISO in gg/mm/aaaa (vuoto se assente).
const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
};

// Pannello Referral "Porta un'Amica" per la scheda cliente (staff).
export function ReferralPanel({ clientId, canEdit }) {
  const [data, setData] = useState(null);
  const [referrer, setReferrer] = useState(null);
  const [busy, setBusy] = useState(false);
  const [friendsOpen, setFriendsOpen] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null); // amica da confermare manualmente

  const load = useCallback(() => {
    api.get(`/clients/${clientId}/referral`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <Card className="p-6 text-sm text-muted-foreground">Caricamento referral…</Card>;

  const pct = Math.min(100, Math.round((data.current_cycle / data.threshold) * 100));
  const friends = data.friends || [];
  const confirmedCount = data.friends_confirmed_count ?? data.completed_count ?? 0;
  const pendingCount = data.friends_pending_count ?? ((data.confirmed_count || 0) + (data.pending_count || 0));
  const totalCount = data.friends_total_count ?? (confirmedCount + pendingCount);

  const copy = async (txt, label) => {
    try { await navigator.clipboard.writeText(txt); toast.success(`${label} copiato`); }
    catch { toast.error("Copia non riuscita"); }
  };
  const share = async () => {
    const text = `Vieni da Hair Identity! Iscriviti con il mio invito e avrai -${data.new_client_discount_pct}% sul primo servizio: ${data.link}`;
    if (navigator.share) { try { await navigator.share({ title: "Porta un'Amica", text, url: data.link }); } catch {} }
    else copy(data.link, "Link");
  };

  const asReferred = data.as_referred;
  const confirmReferrer = async () => {
    if (!referrer) { toast.error("Seleziona la cliente che l'ha presentata"); return; }
    setBusy(true);
    try {
      await api.post(`/referrals/${asReferred.id}/confirm`, { referrer_id: referrer.id });
      toast.success("Referral confermato");
      setReferrer(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore conferma"); }
    finally { setBusy(false); }
  };

  // Verifica retroattiva SOLO per le amiche presentate da questa cliente.
  const verifyReferrals = async () => {
    setVerifying(true);
    try {
      const { data: res } = await api.post(`/referrals/reconcile?referrer_id=${clientId}`);
      const conv = res.converted || 0;
      const pend = res.pending_remaining ?? res.issues_count ?? 0;
      toast.success(`Verifica completata: ${conv} confermate, ${pend} ancora in attesa`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore verifica"); }
    finally { setVerifying(false); }
  };

  // Conferma manuale di un'amica presentata.
  const doManualConfirm = async () => {
    if (!confirmTarget) return;
    setBusy(true);
    try {
      const { data: res } = await api.post(`/referrals/${confirmTarget.id}/confirm-manual`, {});
      toast.success(res.already ? "Amica già confermata" : "Amica confermata");
      setConfirmTarget(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore conferma"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="referral-panel">
      {/* Progresso "Porta un'Amica" (questa cliente come referrer) */}
      <Card className="p-5">
        <div className="flex items-center gap-3 mb-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[hsl(var(--hi-gold)/0.15)] text-[hsl(var(--hi-gold))]"><Users className="h-5 w-5" /></div>
          <div className="flex-1">
            <h3 className="font-heading text-lg font-semibold">Porta un'Amica</h3>
            <p className="text-sm text-muted-foreground">Ogni {data.threshold} amiche <b>confermate</b> = premio -{data.reward_pct}%</p>
          </div>
          {canEdit && (
            <Button variant="outline" size="sm" className="h-8 gap-1.5 shrink-0" onClick={verifyReferrals} disabled={verifying} data-testid="referral-verify">
              <RefreshCw className={`h-3.5 w-3.5 ${verifying ? "animate-spin" : ""}`} /> Verifica segnalazioni
            </Button>
          )}
        </div>
        <div className="flex items-center justify-between text-sm mb-1">
          <span className="text-muted-foreground">Progresso verso il prossimo premio (solo confermate)</span>
          <span className="font-semibold tabular" data-testid="referral-progress-count">{data.current_cycle}/{data.threshold}</span>
        </div>
        <Progress value={pct} className="h-2" />
        <div className="flex flex-wrap gap-2 mt-3 text-xs">
          <Badge className="gap-1 border-transparent bg-[hsl(var(--hi-status-success)/0.15)] text-[hsl(var(--hi-status-success))]" data-testid="referral-confirmed">
            <CheckCircle2 className="h-3 w-3" /> Confermate: {confirmedCount}
          </Badge>
          <Badge className="gap-1 border-transparent bg-[hsl(38_92%_50%/0.15)] text-[hsl(38_92%_35%)]" data-testid="referral-pending">
            <Clock className="h-3 w-3" /> In attesa: {pendingCount}
          </Badge>
          <Badge variant="outline" data-testid="referral-total">Totale segnalate: {totalCount}</Badge>
        </div>

        {/* Elenco espandibile "Amiche presentate" */}
        {totalCount > 0 && (
          <Collapsible open={friendsOpen} onOpenChange={setFriendsOpen} className="mt-4">
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="w-full justify-between h-9 px-2" data-testid="referral-friends-toggle">
                <span className="flex items-center gap-2 text-sm font-medium"><Users className="h-4 w-4" /> Amiche presentate</span>
                <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${friendsOpen ? "rotate-180" : ""}`} />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-2 space-y-2" data-testid="referral-friends-list">
              {friends.map((f) => (
                <div key={f.id} className="rounded-lg border border-border p-3 text-sm" data-testid={`referral-friend-${f.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{f.referred_name}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">Segnalata il {fmtDate(f.created_at) || "—"}</div>
                      <div className="flex items-center gap-1.5 mt-1.5">
                        {f.confirmed ? (
                          <span className="inline-flex items-center gap-1.5 text-[hsl(var(--hi-status-success))] text-xs font-medium">
                            <span className="h-2 w-2 rounded-full bg-[hsl(var(--hi-status-success))]" />
                            Confermata
                            {f.first_sale_date ? ` — prima vendita ${fmtDate(f.first_sale_date)}` : (f.manual_confirmed ? " — manualmente" : "")}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-[hsl(38_92%_35%)] text-xs font-medium">
                            <span className="h-2 w-2 rounded-full bg-[hsl(38_92%_50%)]" />
                            Da confermare — {f.has_sale ? "vendita rilevata" : "nessuna vendita rilevata"}
                          </span>
                        )}
                      </div>
                    </div>
                    {!f.confirmed && canEdit && (
                      <Button size="sm" variant="outline" className="h-8 shrink-0 gap-1" onClick={() => setConfirmTarget(f)} data-testid={`referral-confirm-manual-${f.id}`}>
                        <UserCheck className="h-3.5 w-3.5" /> Conferma
                      </Button>
                    )}
                  </div>
                  {f.confirmed && f.has_sale && (
                    <div className="flex items-center gap-1 text-[11px] text-muted-foreground mt-1.5">
                      <Receipt className="h-3 w-3" /> Vendita/pagamento registrato
                    </div>
                  )}
                </div>
              ))}
            </CollapsibleContent>
          </Collapsible>
        )}
      </Card>

      {/* Codice + link condivisibile */}
      <Card className="p-5 space-y-3">
        <Label className="text-xs">Codice invito</Label>
        <div className="flex gap-2">
          <Input readOnly value={data.code} data-testid="referral-code" className="font-mono h-9" />
          <Button variant="outline" className="h-9 shrink-0" onClick={() => copy(data.code, "Codice")} data-testid="referral-copy-code"><Copy className="h-4 w-4" /></Button>
        </div>
        <Label className="text-xs">Link da condividere</Label>
        <div className="flex gap-2">
          <Input readOnly value={data.link} data-testid="referral-link" className="text-xs h-9" />
          <Button variant="outline" className="h-9 shrink-0" onClick={() => copy(data.link, "Link")}><Copy className="h-4 w-4" /></Button>
          <Button className="h-9 shrink-0 gap-1" onClick={share} data-testid="referral-share"><Share2 className="h-4 w-4" /> Condividi</Button>
        </div>
      </Card>

      {/* Premi sbloccati */}
      {data.rewards?.length > 0 && (
        <Card className="p-5" data-testid="referral-rewards">
          <h4 className="font-medium mb-2 flex items-center gap-2"><Gift className="h-4 w-4 text-[hsl(var(--hi-gold))]" /> Premi sbloccati</h4>
          <div className="space-y-2">
            {data.rewards.map((r) => (
              <div key={r.id} className="flex items-center justify-between rounded-lg border border-border p-2.5 text-sm">
                <div><span className="font-mono">{r.code}</span> · -{Math.round(r.percent || 0)}%</div>
                <div className="flex items-center gap-2">
                  {r.expires_at && <span className="text-[11px] text-muted-foreground">scade {(r.expires_at || "").slice(0, 10)}</span>}
                  <Badge variant={r.status === "unused" ? "secondary" : "outline"} className="text-[10px] capitalize">{r.status === "unused" ? "disponibile" : r.status}</Badge>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Questa cliente come NUOVA cliente referral */}
      {asReferred && (
        <Card className="p-5" data-testid="referral-as-referred">
          <h4 className="font-medium mb-2 flex items-center gap-2"><Gift className="h-4 w-4 text-[hsl(var(--hi-gold))]" /> Presentata da un'amica</h4>
          {asReferred.status === "pending" ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-muted-foreground"><Clock className="h-4 w-4" /> Segnalata{asReferred.referrer_name_raw ? `: "${asReferred.referrer_name_raw}"` : ""} — si conferma automaticamente alla prima vendita</div>
              {canEdit && (
                <>
                  <Label className="text-xs">Collega la cliente che l'ha presentata</Label>
                  <ClientSearch value={referrer} onSelect={setReferrer} />
                  <Button size="sm" onClick={confirmReferrer} disabled={busy || !referrer} data-testid="referral-confirm">Collega referente</Button>
                </>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle2 className="h-4 w-4 text-[hsl(var(--hi-status-success))]" />
              <span>{asReferred.status === "completed" ? "Confermata (prima visita completata)" : "Segnalata (in attesa della prima visita)"}</span>
              {asReferred.status === "confirmed" && !asReferred.discount_applied && (
                <Badge variant="secondary" className="ml-1">Sconto benvenuto -{data.new_client_discount_pct}% disponibile alla prima visita</Badge>
              )}
            </div>
          )}
        </Card>
      )}

      {/* Dialog conferma manuale */}
      <AlertDialog open={!!confirmTarget} onOpenChange={(o) => { if (!o) setConfirmTarget(null); }}>
        <AlertDialogContent data-testid="referral-manual-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Conferma amica presentata</AlertDialogTitle>
            <AlertDialogDescription>
              Confermare <b>{confirmTarget?.referred_name}</b> come amica presentata e servita?
              Questa operazione incrementerà il contatore referral della cliente che l'ha invitata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="referral-manual-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={doManualConfirm} disabled={busy} data-testid="referral-manual-confirm">Conferma</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
